import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  Output,
  type Signal,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Currency, Money } from '@principle-theorem/accounting';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { DepositLifecycle } from '@principle-theorem/principle-core';
import {
  DepositRequirementSource,
  DepositStatus,
  type DepositPaymentMode,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import { DEPOSIT_REQUIRED_WARNING_CLASSES } from '../../appointment-view-sidebar/appointment-deposit-sidebar/deposit-required-display';
import {
  DEPOSIT_SOURCE_LABELS,
  DEPOSIT_SOURCE_TOOLTIPS,
} from '../../appointment-view-sidebar/appointment-deposit-sidebar/deposit-source-display';
import { IWaiveDepositDialogResult } from '../../appointment-view-sidebar/appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.component';
import { DepositActionsComponent } from '../deposit-actions/deposit-actions.component';
import {
  DepositAbsenceKind,
  type IDepositAbsence,
} from '../deposit-status-chip/deposit-chip-display';
import { type IDepositRequirementContributor } from '../deposit/deposit.store';

@Component({
  selector: 'pr-appointment-create-deposit-section',
  templateUrl: './appointment-create-deposit-section.component.html',
  host: { class: 'flex flex-col gap-2' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    DepositActionsComponent,
  ],
})
export class AppointmentCreateDepositSectionComponent {
  readonly depositAmount = input<number | undefined>(undefined);
  readonly depositDefaultAmount = input<number | undefined>(undefined);
  readonly depositSource = input<DepositRequirementSource | undefined>(
    undefined
  );
  readonly depositWarning = input<string | undefined>(undefined);
  readonly depositOverrideWarning = input<string | undefined>(undefined);
  readonly depositMetadata = input<IDepositMetadata | undefined>(undefined);
  readonly currency = input<Currency>(Currency.AUD);
  /**
   * Whether the deposit will hold the card or charge it, resolved for this
   * booking. Only used before a deposit exists, when the chip has no metadata
   * of its own to say so.
   */
  readonly paymentMode = input<DepositPaymentMode | undefined>(undefined);
  readonly canOverrideDeposit = input(false);
  readonly depositRequired = input(false);
  readonly optedIn = input(false);
  readonly readOnly = input(false);
  readonly depositRequiredBy = input<IDepositRequirementContributor[]>([]);

  @Output() waiveDeposit = new EventEmitter<IWaiveDepositDialogResult>();
  @Output() amountOverridden = new EventEmitter<number>();
  @Output() amountResetToDefault = new EventEmitter<void>();
  @Output() optInChanged = new EventEmitter<boolean>();

  DepositStatus = DepositStatus;

  readonly depositRequiredWarningClasses = DEPOSIT_REQUIRED_WARNING_CLASSES;

  readonly sourceLabel = computed(() =>
    this.getSourceLabel(this.depositSource())
  );
  readonly sourceTooltip = computed(() =>
    this.getSourceTooltip(this.depositSource())
  );
  readonly depositInPlay = computed(
    () => this.depositRequired() || this.optedIn()
  );
  readonly canOptIn = computed(
    () =>
      !this.readOnly() &&
      this.canOverrideDeposit() &&
      this.depositMetadataIsAbsentOrSafeToDiscard() &&
      !this.depositRequired()
  );
  readonly canEditAmount = computed(
    () =>
      this.depositInPlay() &&
      !this.readOnly() &&
      this.canOverrideDeposit() &&
      !this.depositMetadata()
  );
  /**
   * What the chip says before any deposit exists. The amount only rides on the
   * chip when the amount field is not there to show it, so it is never stated
   * twice.
   */
  readonly depositAbsence: Signal<IDepositAbsence | undefined> = computed(
    () => {
      if (this.depositMetadata()) {
        return;
      }
      if (!this.depositInPlay()) {
        return {
          kind: DepositAbsenceKind.NotRequired,
          paymentMode: this.paymentMode(),
        };
      }
      if (this.canEditAmount()) {
        return {
          kind: DepositAbsenceKind.NotRequested,
          paymentMode: this.paymentMode(),
        };
      }
      return {
        kind: DepositAbsenceKind.NotRequested,
        amountCents: this.depositAmount(),
        currency: this.currency(),
        paymentMode: this.paymentMode(),
      };
    }
  );
  readonly canResetAmount = computed(() => {
    const defaultAmount = this.depositDefaultAmount();
    return (
      this.canEditAmount() &&
      defaultAmount !== undefined &&
      this.depositAmount() !== defaultAmount
    );
  });
  readonly requiredByLabels = computed(() =>
    this.depositRequiredBy().map((contributor) =>
      contributor.quantity > 1
        ? contributor.name + ' ×' + contributor.quantity
        : contributor.name
    )
  );
  readonly resetAmountTooltip = computed(
    () =>
      'Reset to the default amount of ' +
      this.formatAmount(this.depositDefaultAmount())
  );
  readonly depositMetadataIsAbsentOrSafeToDiscard = computed(() => {
    const metadata = this.depositMetadata();
    return !metadata || DepositLifecycle.canDiscardStaffAddedMetadata(metadata);
  });

  /**
   * Always two decimal places, so the editable amount reads like every other
   * amount in the section. Only safe because the input commits on blur:
   * reformatting on each keystroke would rewrite the value under the cursor.
   */
  get depositAmountDollars(): string | undefined {
    const depositAmount = this.depositAmount();
    if (depositAmount === undefined) {
      return;
    }
    return Money.fromCents(depositAmount).toFixed(2);
  }

  set depositAmountDollars(value: number | undefined) {
    if (value !== undefined) {
      this.amountOverridden.emit(Money.toCents(value));
    }
  }

  getSourceLabel(source?: DepositRequirementSource): string {
    return DEPOSIT_SOURCE_LABELS[source ?? DepositRequirementSource.None];
  }

  getSourceTooltip(source?: DepositRequirementSource): string {
    return DEPOSIT_SOURCE_TOOLTIPS[source ?? DepositRequirementSource.None];
  }

  formatAmount(amountInCents?: number): string {
    if (amountInCents === undefined) {
      return '';
    }
    return Money.formatPlainCents(
      amountInCents,
      this.depositMetadata()?.currency ?? this.currency()
    );
  }

  resetAmount(): void {
    if (!this.canResetAmount()) {
      return;
    }
    this.amountResetToDefault.emit();
  }
}
